import { createFileRoute } from "@tanstack/react-router"
import { Check, X, Zap, AlertCircle, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { LifetimeAccessPricing } from "@/components/life-time-access-pricing"
import { createLLMOMeta, createBreadcrumbSchema, createFAQSchema, createArticleSchema } from "@/lib/seo"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import { Paragraph, ContentSection } from "@/components/tools/SeoContent"
import { useState } from "react"
import { AskAIExplain } from "@/components/ask-ai-to-explain"

// ─── FAQ Data ────────────────────────────────────────────────
const faqs = [
  {
    question: "Is Zapify better than CreatorFlow?",
    answer:
      "Zapify is better than CreatorFlow for Instagram creators who want AI-powered replies, brand voice controls, growth attribution showing followers and leads gained, ask-to-follow flows, smart offers, detailed Instagram content insights, 10+ language support, a referral program with 20% recurring commission, and a limited lifetime access option. CreatorFlow is better for creators who want a permanent free plan with 500 DMs per month, the simplest possible single-message automation, flat per-DM-volume pricing, real-time phone preview, geo click analytics, and a contact CRM with CSV export.",
  },
  {
    question: "What is the best CreatorFlow alternative?",
    answer:
      "Zapify is a strong CreatorFlow alternative for Instagram creators who want AI-powered automation, growth attribution, followers and leads gained tracking, ask-to-follow flows, smart offers, Reel and post insights, multiple Instagram accounts, and creator-friendly pricing including a limited lifetime access offer. If your primary goal is to turn Instagram comments into DMs and grow your account with AI assistance, Zapify is the most direct upgrade from CreatorFlow.",
  },
  {
    question: "Does Zapify have AI replies?",
    answer:
      "Yes. Zapify is positioned as an AI-powered Instagram comment management and automation platform. It uses AI to generate contextual DM replies with brand voice controls for tone, style, reply length, emoji use, and hashtag use. CreatorFlow does not currently offer AI-powered replies at any pricing tier.",
  },
  {
    question: "Does CreatorFlow have AI replies?",
    answer:
      "No. CreatorFlow does not currently offer AI-powered responses. Each automation sends one pre-written message to anyone who triggers the keyword. If you need AI to generate contextual, brand-voice replies to Instagram comments, you need a different tool. CreatorFlow's pricing and feature pages clearly confirm that AI is not available in any plan.",
  },
  {
    question: "Why switch from CreatorFlow to Zapify?",
    answer:
      "Switch to Zapify if you want AI replies that sound like your brand, want to know how many followers and leads your automations actually generated, want smart offers and deeper conversion tools, want detailed Instagram Reel and post metrics alongside your automation data, want 10+ language support, or want the option to lock in lifetime access instead of paying monthly indefinitely.",
  },
  {
    question: "How does Zapify pricing compare with CreatorFlow?",
    answer:
      "Zapify's brand brief lists a Starter plan at $9.99/month with unlimited DMs, 10,000 AI replies, 3 Instagram profiles, and 3 team members — plus a limited $200 lifetime access offer. CreatorFlow charges $0 for 500 DMs/month (permanent free), $15/month for 5,000 DMs and 2 workspaces (Pro), and $30/month for 10,000 DMs and 5 workspaces (Growth). Zapify is cheaper at the entry paid tier and includes AI; CreatorFlow has a stronger permanent free plan. Neither charges per contact.",
  },
  {
    question: "Does Zapify support Instagram Story DM automation?",
    answer:
      "Yes. Zapify supports Story-triggered DM automations. Creators can automate replies and follow-up messages from Story reactions and replies, in addition to post and Reel comment automation. CreatorFlow also supports Story reply automation on all paid plans.",
  },
  {
    question: "Does CreatorFlow support ask-to-follow flows?",
    answer:
      "Yes. CreatorFlow supports a Follow Gate feature that asks users to follow your account before receiving the link. Zapify also supports ask-to-follow flows. Both tools have this feature, but Zapify pairs it with AI replies and growth attribution so you can measure how many followers the campaign generated.",
  },
  {
    question: "Can I collect emails with Zapify?",
    answer:
      "Yes. Zapify supports email capture inside Instagram DM conversations. Creators can collect emails from people who engage with their automations. CreatorFlow also supports email capture (Email Gate) on Pro and Growth plans at $15/month and $30/month respectively.",
  },
  {
    question: "Does Zapify have a free plan?",
    answer:
      "Yes. Zapify offers a free trial period with 3 automations, 1,000 DMs, 1,000 AI replies, and 1 Instagram profile. Unlike CreatorFlow's permanent free tier, Zapify's free period is time-limited at 30 days. After that, creators choose a paid plan starting at $9.99/month. The Zapify free trial includes AI replies, which CreatorFlow's free plan does not.",
  },
  {
    question: "Does CreatorFlow have a free plan?",
    answer:
      "Yes. CreatorFlow's free plan is permanent — no time limit, no credit card required. It includes 500 automated DMs per month, 1 workspace, unlimited automations, comment-to-DM, story reply automation, and keyword triggers. The free plan does not include AI replies, email gate, follow gate, link click tracking, geo analytics, or CSV export. Those require the Pro plan at $15/month.",
  },
  {
    question: "Can I manage multiple Instagram accounts with Zapify?",
    answer:
      "Yes. Zapify supports up to 3 Instagram profiles on the Starter plan and up to 6 on the Professional plan. CreatorFlow supports up to 2 workspaces on Pro ($15/month) and up to 5 workspaces on Growth ($30/month), with each workspace connecting to one Instagram account.",
  },
  {
    question: "Who should still consider CreatorFlow over Zapify?",
    answer:
      "CreatorFlow is the better fit if you want a permanent free plan with no time pressure, prefer the simplest single-message automation setup with no AI configuration, want flat pricing that scales only with DM volume (not contacts), want a real-time phone preview before activating automations, need a built-in contact CRM with CSV export to Mailchimp or ConvertKit, or value a platform with 14,000+ verified users and Trustpilot reviews. If you do not need AI replies or growth attribution, CreatorFlow is a solid, clean tool.",
  },
  {
    question: "Is Zapify Meta approved?",
    answer:
      "Zapify's current homepage displays \"Approved by Meta.\" CreatorFlow is verified as a Meta Tech Provider since January 2026.",
  },
]

// ─── Feature Table Data ──────────────────────────────────────
type FeatureRow = {
  name: string
  zapify: string | boolean
  creatorflow: string | boolean
  zapifyNote?: string
  creatorflowNote?: string
}

type FeatureCategory = {
  category: string
  rows: FeatureRow[]
}

const featureCategories: FeatureCategory[] = [
  {
    category: "Core Automation",
    rows: [
      { name: "Comment-to-DM automation", zapify: true, creatorflow: true, zapifyNote: "Keyword triggers on posts, Reels, any post", creatorflowNote: "Keyword triggers on posts and Reels" },
      { name: "Story DM automation", zapify: true, creatorflow: true, zapifyNote: "Story reply triggers supported", creatorflowNote: "Story reply automation on all plans" },
      { name: "Single-post automation", zapify: true, creatorflow: true },
      { name: "Multiple selected posts", zapify: true, creatorflow: true, creatorflowNote: "Pick specific posts" },
      { name: "Any-post automation", zapify: true, creatorflow: true, creatorflowNote: "Trigger on any post" },
      { name: "Keyword triggers", zapify: true, creatorflow: true, zapifyNote: "Up to 20 keywords per automation", creatorflowNote: "Up to 20 keywords per automation" },
      { name: "Ask-to-follow before content", zapify: true, creatorflow: true, creatorflowNote: "Follow Gate" },
      { name: "Email capture in DMs", zapify: true, creatorflow: true, creatorflowNote: "Email Gate (Pro/Growth plans)" },
      { name: "Opening/intro DM before main message", zapify: true, creatorflow: true, creatorflowNote: "Opening DM option" },
    ],
  },
  {
    category: "AI & Intelligence",
    rows: [
      { name: "AI-powered replies", zapify: true, creatorflow: false, zapifyNote: "AI replies built into platform", creatorflowNote: "Single pre-written message only" },
      { name: "AI brand voice controls", zapify: true, creatorflow: false, zapifyNote: "Tone, style, emoji, hashtag, reply length" },
      { name: "Smart offers / link cards", zapify: true, creatorflow: false, creatorflowNote: "Not publicly listed" },
      { name: "Templates for quick setup", zapify: true, creatorflow: true, zapifyNote: "Creator-focused templates", creatorflowNote: "Template library by use case" },
    ],
  },
  {
    category: "Analytics & Growth",
    rows: [
      { name: "Detailed Instagram Reel/post insights", zapify: true, creatorflow: false, zapifyNote: "Views, reach, likes, comments, shares, saves, visits, taps, engagement", creatorflowNote: "Analytics cover automation CTR only" },
      { name: "Followers gained attribution", zapify: true, creatorflow: false, zapifyNote: "Zapify tracks followers gained through automation" },
      { name: "Leads/contacts gained attribution", zapify: true, creatorflow: false, zapifyNote: "Zapify tracks leads gained", creatorflowNote: "Contacts stored but no growth attribution" },
      { name: "Link click tracking", zapify: true, creatorflow: true, creatorflowNote: "Click CTR per automation" },
      { name: "Geo analytics", zapify: true, creatorflow: true, creatorflowNote: "Country and city breakdown" },
      { name: "Contact CRM", zapify: true, creatorflow: true, creatorflowNote: "Contacts with email, source, last interaction" },
      { name: "Real-time phone preview", zapify: false, creatorflow: true, zapifyNote: "Not confirmed in brief", creatorflowNote: "Live phone mockup before publishing" },
    ],
  },
  {
    category: "Platform & Pricing",
    rows: [
      { name: "Free plan", zapify: "30-day trial with AI", creatorflow: "Permanent 500 DMs/mo, no AI" },
      { name: "Entry paid plan", zapify: "$9.99/mo (Starter)", creatorflow: "$15/mo (Pro)" },
      { name: "Professional / Growth", zapify: "$19.99/mo", creatorflow: "$30/mo (Growth)" },
      { name: "Lifetime access", zapify: "$200 once", creatorflow: "Not available" },
      { name: "Multiple Instagram accounts", zapify: true, creatorflow: true, zapifyNote: "Up to 6 on Professional", creatorflowNote: "Up to 5 workspaces on Growth ($30/mo)" },
      { name: "Team access", zapify: true, creatorflow: true, zapifyNote: "Plan dependent", creatorflowNote: "Growth plan, 5 seats per workspace" },
      { name: "Language switching", zapify: true, creatorflow: false, zapifyNote: "10+ languages", creatorflowNote: "App in EN/PT/ES; DM content in any language" },
      { name: "CSV export", zapify: false, creatorflow: true, zapifyNote: "Not confirmed in brief", creatorflowNote: "Export to Mailchimp, ConvertKit, Beehiiv" },
      { name: "DM top-up packs", zapify: false, creatorflow: true, zapifyNote: "Not confirmed in brief", creatorflowNote: "1K–5K non-expiring packs" },
      { name: "Referral / affiliate program", zapify: true, creatorflow: true, zapifyNote: "20% recurring commission", creatorflowNote: "Check current terms" },
      { name: "Meta approval", zapify: true, creatorflow: true, zapifyNote: "Approved by Meta", creatorflowNote: "Meta Tech Provider since Jan 2026" },
    ],
  },
]

// ─── Pricing Data ────────────────────────────────────────────
const pricingTiers = [
  { label: "Free plan", zapify: "$0 — 30-day trial, 1K DMs, 1K AI replies", creatorflow: "$0 — Permanent, 500 DMs/mo, no AI", zapifyWidth: "2%", creatorflowWidth: "2%" },
  { label: "Entry paid plan", zapify: "$9.99/mo — unlimited DMs, 10K AI replies, 3 profiles", creatorflow: "$15/mo (Pro) — 5,000 DMs, 2 workspaces, no AI", zapifyWidth: "20%", creatorflowWidth: "30%" },
  { label: "Professional / Growth", zapify: "$19.99/mo — unlimited everything, 6 profiles", creatorflow: "$30/mo (Growth) — 10K DMs, 5 workspaces", zapifyWidth: "38%", creatorflowWidth: "60%" },
  { label: "Lifetime access", zapify: "$200 once (limited)", creatorflow: "Not available", zapifyWidth: "30%", creatorflowWidth: "0%" },
]

// ─── Route ───────────────────────────────────────────────────
export const Route = createFileRoute("/comparison/creatorflow-vs-zapify-pro")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "Zapify vs CreatorFlow: Which Instagram DM Automation Tool Is Better for Creators?",
        description:
          "Compare Zapify vs CreatorFlow for Instagram DM automation, AI replies, comment-to-DM, Story automation, pricing, and creator growth features. See which tool wins for 2026.",
        url: "https://zapify.pro/comparison/creatorflow-vs-zapify-pro",
        publishedTime: "2026-06-09",
        modifiedTime: "2026-06-09",
        ogType: "article",
      }),
      { property: "og:image", content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png" },
      { property: "og:locale", content: "en_US" },
      { name: "keywords", content: "Zapify vs CreatorFlow, CreatorFlow vs Zapify, CreatorFlow alternative, Instagram DM automation tool, Instagram comment to DM automation, AI Instagram replies, Instagram automation for creators, best Instagram DM tool 2026, comment to DM automation tool" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/comparison/creatorflow-vs-zapify-pro" },
    ],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Comparisons", item: "https://zapify.pro/comparison" },
          { position: 3, name: "CreatorFlow vs Zapify", item: "https://zapify.pro/comparison/creatorflow-vs-zapify-pro" },
        ],
      }),
      createFAQSchema(faqs),
      createArticleSchema({
        headline: "CreatorFlow vs Zapify Pro 2026: Instagram DM Automation Compared",
        description: "Comprehensive comparison of CreatorFlow and Zapify Pro for Instagram DM automation in 2026.",
        image: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
        author: { name: "Zapify", url: "https://zapify.pro" },
        datePublished: "2026-06-09",
        dateModified: "2026-06-09",
        publisher: {
          name: "Zapify Pro",
          url: "https://zapify.pro",
          logo: "https://zapify.pro/logo-small.png",
          description: "AI-Powered Instagram Comment Management & Automation",
        },
      }),
    ],
  }),
  component: CreatorFlowComparisonPage,
})

// ─── Page Component ──────────────────────────────────────────
export function CreatorFlowComparisonPage() {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()
  const [openFaq, setOpenFaq] = useState(0)

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* ═══ Section 1: Hero ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 shadow-sm mb-6">
            <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-xs">Updated June 2026</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight max-w-5xl mx-auto">
            Zapify vs{" "}
            <span className="gradient-text">CreatorFlow</span>
            : Which Instagram DM Automation Tool Is Better for Creators?
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-neutral-600 dark:text-neutral-400 font-medium max-w-2xl mx-auto mb-8 sm:mb-10">
            Both Zapify and CreatorFlow help creators turn Instagram comments into DMs. But if you want AI replies, brand voice control, growth attribution, ask-to-follow flows, Story DMs, and the ability to track which automations actually grew your account — Zapify is the more complete choice.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              onClick={() => handleCTA("CTA_Comparison_Hero", "Start Free No Credit Card")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-8 py-6 text-base rounded-full shadow-lg"
            >
              Start Free No Credit Card
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="px-8 py-6 text-base rounded-full font-semibold border-neutral-200 dark:border-neutral-700"
              onClick={() => {
                document.getElementById("feature-table")?.scrollIntoView({ behavior: "smooth" })
              }}
            >
              See Full Comparison
            </Button>
          </div>
        </section>

        {/* ═══ Ask AI to Explain ═══ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <AskAIExplain
            context="comparison"
            meta={{ competitorName: "CreatorFlow", pageUrl: "https://zapify.pro/comparison/creatorflow-vs-zapify-pro" }}
            isCard
            isWidget
          />
        </section>

        {/* ═══ Section 2: Quick Answer Box ═══ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20" aria-label="Featured summary">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-button mb-3">
              Quick Answer - How does CreatorFlow compare to Zapify?
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
              <strong className="text-neutral-900 dark:text-white">Choose Zapify for AI-powered replies, Instagram growth attribution, ask-to-follow flows, smart offers, templates, multiple Instagram accounts, and creator-friendly pricing including a limited lifetime access offer.</strong>{" "}
              Choose CreatorFlow if you want a simpler single-message automation tool with{" "}
              <strong className="bg-clip-text text-transparent bg-gradient-to-r from-brand-accent to-brand-accent">
                flat-rate per-DM pricing, geo analytics, real-time phone preview, and no AI features
              </strong>
              . CreatorFlow does not offer AI-powered replies, does not track followers or leads gained through automation, and has no lifetime access offer. For creators who want automation that actively drives growth — not just sends messages — Zapify closes the gap.
            </p>
          </article>
        </section>

        {/* ═══ Section 3: Feature Comparison Table ═══ */}
        <section id="feature-table" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              How does Zapify compare to CreatorFlow feature{" "}
              <span className="gradient-text">by feature?</span>
            </h2>
            <Paragraph className="max-w-3xl mx-auto">
              A transparent, side-by-side breakdown of every Instagram automation capability that matters in 2026.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm bg-white dark:bg-neutral-900/50">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[40%]">
                      Feature
                    </th>
                    <th className="text-left px-4 sm:px-6 py-4 uppercase tracking-wider text-xs w-[30%] text-green-700 dark:text-green-300">
                      Zapify
                    </th>
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[30%]">
                      CreatorFlow
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {featureCategories.flatMap((cat) => [
                    <tr key={`cat-${cat.category}`} className="bg-neutral-50/70 dark:bg-neutral-900/70">
                      <td colSpan={3} className="px-4 sm:px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-brand-button">
                        {cat.category}
                      </td>
                    </tr>,
                    ...cat.rows.map((row) => (
                      <tr key={`${cat.category}-${row.name}`} className="hover:bg-neutral-50/50 dark:hover:bg-neutral-800/20 transition-colors align-top">
                        <td className="px-4 sm:px-6 py-3.5 font-medium text-neutral-700 dark:text-neutral-300">{row.name}</td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{typeof row.zapify === "boolean" ? (row.zapify ? "Yes" : "No") : row.zapify}</span>
                            {row.zapifyNote ? <span className="text-xs text-green-700 dark:text-green-300">{row.zapifyNote}</span> : null}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{typeof row.creatorflow === "boolean" ? (row.creatorflow ? "Yes" : "No") : row.creatorflow}</span>
                            {row.creatorflowNote ? <span className="text-xs text-red-600 dark:text-red-300">{row.creatorflowNote}</span> : null}
                          </div>
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ═══ Section 4: Pricing Breakdown ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              CreatorFlow's pricing vs{" "}
              <span className="gradient-text">Zapify's AI-included pricing</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Zapify is the better deal if AI replies matter to you — you get them at $9.99/month Starter, and CreatorFlow doesn't offer AI at any price point. For the full growth stack — AI, attribution, multiple accounts, lifetime access — Zapify wins.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Plan stage</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300">Zapify</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">CreatorFlow</th>
                </tr>
              </thead>
              <tbody>
                {pricingTiers.map((tier) => (
                  <tr key={tier.label} className="align-top border-b last:border-b-0 border-neutral-100 dark:border-neutral-800/80">
                    <td className="px-4 sm:px-5 py-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">{tier.label}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-green-700 dark:text-green-300">{tier.zapify}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-neutral-700 dark:text-neutral-100">{tier.creatorflow}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ═══ Lifetime Access Pricing ═══ */}
        <LifetimeAccessPricing
          spotsLeft={87}
          totalSpots={100}
          price={200}
          originalMonthlyPrice={30}
          onGetAccess={() => handleCTA("CTA_CreatorFlow_Lifetime_Pricing", "Claim Lifetime Access")}
        />

        {/* ═══ Section 5: Better Value Breakdown ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              AI-Powered Growth vs{" "}
              <span className="gradient-text">Simple DM Delivery</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              CreatorFlow sends one pre-written message to everyone who comments. Zapify uses AI to generate replies that vary by context, match your brand voice, and feel more like a real conversation.
            </Paragraph>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Zapify Value */}
            <div className="rounded-2xl border-2 border-green-200 dark:border-green-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-green-600 dark:text-green-400">Zapify - AI-Powered Growth</h3>
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                AI replies + growth attribution:
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-3 text-sm text-green-700 dark:text-green-300 mb-4">
                <Check className="inline w-4 h-4 mr-1" /> AI-powered contextual replies with brand voice + followers & leads gained tracking
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                Creator economics:
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-3 text-sm text-green-700 dark:text-green-300">
                <Check className="inline w-4 h-4 mr-1" /> $9.99/mo with AI included + $200 lifetime access option + 20% referral commission
              </div>

              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-4">
                AI replies. Growth attribution. Lifetime access. Lower prices.
              </p>
            </div>

            {/* CreatorFlow Simple */}
            <div className="rounded-2xl border-2 border-red-200 dark:border-red-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-red-500 dark:text-red-400">CreatorFlow - Simple DM Delivery</h3>
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                Single pre-written message:
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300 mb-4">
                <X className="inline w-4 h-4 mr-1" /> No AI replies — same canned message to every commenter, no growth attribution
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                Pricing and access:
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300">
                <X className="inline w-4 h-4 mr-1" /> No lifetime access — $15/mo Pro, $30/mo Growth, no AI at any price
              </div>

              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-4">
                Clean DM delivery. Permanent free tier. No AI. No growth attribution.
              </p>
            </div>
          </div>
        </section>

        {/* ═══ Section 6: FAQ ═══ */}
        <section className="w-full py-12 sm:py-20 px-4 sm:px-6 border-b border-neutral-100 dark:border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-[#181C31] dark:text-white tracking-tight">
                Frequently <span className="gradient-text">Asked Questions</span>
              </h2>
              <p className="text-sm sm:text-base md:text-xl text-[#64657E] dark:text-gray-400 font-medium max-w-2xl mx-auto mt-3 sm:mt-5">
                Everything you need to know about CreatorFlow vs Zapify
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index
                return (
                  <div key={faq.question} className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 sm:px-6 sm:py-4 dark:bg-card/80">
                    <button type="button" onClick={() => setOpenFaq((prev) => (prev === index ? -1 : index))} className="flex w-full items-center justify-between gap-3 text-left">
                      <div className="flex items-center gap-3">
                        <span className="text-base leading-none">{["⚡", "🔍", "🤖", "🤖", "🔄", "💰", "📱", "👤", "📧", "🆓", "🆓", "📸", "🏢", "✅"][index] ?? "⚡"}</span>
                        <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.15rem]">{faq.question}</p>
                      </div>
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">{isOpen ? "−" : "+"}</span>
                    </button>
                    <div className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}>
                      <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">{faq.answer}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ═══ Section 7: Bottom CTA ═══ */}
        <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6">
          {/* Gradient blob */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight">
              Ready to grow with <span className="gradient-text">AI-powered automation?</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-8 sm:mb-10 max-w-xl mx-auto">
              AI replies, growth attribution, followers gained tracking, smart offers, and lifetime access. Try Zapify free and see the full picture of what your automation is doing to your business.
            </p>

            <Button
              onClick={() => handleCTA("CTA_Comparison_Bottom", "Start Free with Zapify")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-10 py-6 text-lg rounded-full shadow-lg"
            >
              Start Free with Zapify
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              30-day free plan &middot; No credit card &middot; 1-minute setup
            </p>
          </div>
        </section>

        {/* Other Comparisons */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-neutral-100 dark:border-white/5">
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
              Compare Zapify vs <span className="gradient-text">other tools</span>
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: "Zapify vs ManyChat", href: "/comparison/manychat-vs-zapify-pro" },
              { label: "Zapify vs LinkDM", href: "/comparison/linkdm-vs-zapify-pro" },
              { label: "Zapify vs Zorcha", href: "/comparison/zorcha-vs-zapify-pro" },
              { label: "Zapify vs Inrō", href: "/comparison/inro-vs-zapify-pro" },
              { label: "Zapify vs LinktoDM", href: "/comparison/linktodm-vs-zapify-pro" },
              { label: "Zapify vs InstantDM", href: "/comparison/instantdm-vs-zapify-pro" },
            ].map((link) => (
              <a key={link.href} href={link.href} className="px-5 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all duration-200">
                {link.label}
              </a>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <ContentSection className="max-w-4xl mx-auto px-4 sm:px-6 pb-8">
          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
            CreatorFlow is a trademark of its respective owner. Zapify is not affiliated with, endorsed by, or sponsored by CreatorFlow. Feature and pricing information is based on publicly available information and Zapify research as of June 6, 2026. Always check each provider's website for current pricing and plan details.
          </p>
        </ContentSection>
      </div>
    </div>
  )
}
